import React, { useCallback, useMemo, useState, useEffect } from 'react';

import DataAdminServiceFactory from '../services/DataAdminServiceFactory';
import { CaseFolder, SiteModel } from '../models/DataAdminTypes';
import { formatDate } from '../utils/dateUtils';
import { NewCaseFolderModal } from './modals/NewCaseFolderModal';
import SortableHeader from '../controls/sortableHeader';

interface CaseFoldersProps {
  getAccessToken: () => Promise<string>;
  env: string;
  site: SiteModel|null;
  onSelectFolder: (folder: string) => void;
}

const CaseFolders: React.FC<CaseFoldersProps> = (
  { getAccessToken, env, site, onSelectFolder }
) => {
  const [caseFolders, setCaseFolders] = useState<CaseFolder[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{key: string|null, direction: string|null}>({ key: null, direction: 'asc' });
  const [showNewCaseFolderModal, setShowNewCaseFolderModal] = useState(false);

  // Memoize service so dependency arrays stay stable
  const dataAdminService = useMemo(
    () => new DataAdminServiceFactory().create(env),
    [env]
  );

  const fetchCaseFolders = useCallback(async () => {
    if (!site) {
      setError('No site selected');
      setLoading(false);
      return [];
    }
    try {
      setLoading(true);
      setError(null);
      const folders = await dataAdminService.getCaseFoldersForSite(await getAccessToken(), site.siteCode);
      setCaseFolders(folders);
    } catch (err) {
      setError('Failed to fetch case folders');
      console.error('Error fetching case folders:', err);
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, site, dataAdminService]);

  useEffect(() => {
    if (site) {
      fetchCaseFolders();
    }
  }, [getAccessToken, dataAdminService, env, fetchCaseFolders, site]);

  const handleSort = (key: string): void => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleNewCaseFolderModalClose = (navToCaseFolder: string|null): void => {
    setShowNewCaseFolderModal(false);

    // Refresh case folders after potentially creating a new one
    fetchCaseFolders();

    if (navToCaseFolder) {
      onSelectFolder(navToCaseFolder);
    }
  };

  // Sort the data based on current sort config
  const sortedFolders = [...caseFolders].sort((a, b) => {
    if (!sortConfig.key) return 0;

    const aValue = a[sortConfig.key as keyof CaseFolder];
    const bValue = b[sortConfig.key as keyof CaseFolder];

    if (aValue && bValue) {
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      } else {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
    }
    return 0;
  });

  if (!site) {
    return <div className="text-center">Select a Site to view Case Folders</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (loading) {
    return <div className="text-center">Loading case folders...</div>;
  }

  return (
    <>
      <div className="content-panel-header">
        <h3 className="text-lg font-semibold text-gray-900 truncate">
          Case Folders
        </h3>
        <button 
          onClick={() => setShowNewCaseFolderModal(true)}
          className={'action-primary'}
        >
          <span>Create New</span>
        </button>
      </div>
      <div className="h-screen min-h-40 overflow-auto">

        {caseFolders.length === 0 && <div>No case folders found for site: {site?.siteName}</div>}

        {caseFolders.length > 0 && (
          <table className="w-full table-scrollable">
            <thead>
              <tr>
                <SortableHeader 
                  label="Folder Name"
                  sortKey="folderName"
                  onSort={handleSort}
                  sortConfig={sortConfig}
                />
                <SortableHeader
                  label="Last Modified"
                  sortKey="lastModifiedDate"
                  onSort={handleSort}
                  sortConfig={sortConfig}
                />
              </tr>
            </thead>
            <tbody>
              {sortedFolders.map(folder => (
                <tr
                  className="table-scrollable-row"
                  key={folder.folderName}
                  onClick={() => onSelectFolder(folder.folderName)}
                >
                  <td>{folder.folderName}</td>
                  <td>{folder.lastModifiedDate ? formatDate(folder.lastModifiedDate) : 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

      </div>

      {showNewCaseFolderModal && (
        <NewCaseFolderModal
          getAccessToken={getAccessToken}
          env={env}
          site={site}
          onClose={folder => handleNewCaseFolderModalClose(folder)}
        />
      )}

    </>
  );
};

export default CaseFolders;
